import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Search, X, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { getSelectUnities } from "@/Api/unities";

export type BankAccountsFilters = {
  name?: string;
  unity_id?: string;
};

type BankAccountsFiltersProps = {
  filters: BankAccountsFilters;
  onFiltersChange: (filters: BankAccountsFilters) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
};

export const BankAccountsFiltersComponent = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
}: BankAccountsFiltersProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [unities, setUnities] = useState<Array<{ id: string; name: string }>>(
    []
  );

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getSelectUnities();
        setUnities(data?.response || []);
      } catch (error) {
        console.error("Erro ao carregar unidades:", error);
      }
    })();
  }, []);

  const updateFilter = (key: keyof BankAccountsFilters, value?: string) => {
    onFiltersChange({ ...filters, [key]: value || undefined });
  };

  const hasActiveFilters = Object.values(filters).some(Boolean);

  return (
    <Card className="mb-4">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold">Filtros</CardTitle>
          <div className="flex gap-2">
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={onClearFilters}
                className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 bg-red-50"
              >
                <X className="w-4 h-4 mr-1" />
                Limpar
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className={cn(
                "h-8",
                isExpanded &&
                  cn(colors.bg_primary, "text-white hover:text-white")
              )}
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-1" />
                  Ocultar
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-1" />
                  Expandir
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="space-y-4 pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="filter-name">Nome</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="filter-name"
                  placeholder="Pesquisar por nome"
                  value={filters.name || ""}
                  onChange={(e) => updateFilter("name", e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && onApplyFilters()}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="filter-unity">Unidade</Label>
              <Select
                value={filters.unity_id || "all"}
                onValueChange={(value) =>
                  updateFilter("unity_id", value !== "all" ? value : undefined)
                }
              >
                <SelectTrigger id="filter-unity">
                  <SelectValue placeholder="Todas as unidades" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {unities.map((unity) => (
                    <SelectItem key={unity.id} value={String(unity.id)}>
                      {unity.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={onApplyFilters}
              className={cn("px-6", colors.bg_primary)}
            >
              <Search className="w-4 h-4 mr-2" />
              Aplicar Filtros
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};
